import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type AgentDto, AgentsService } from '@inference.engine/api-client/web';
import { logger } from '@/lib/utils/logger';
import { usePermissions } from '@/entities/user';
import type { UseSpringboardToolsOptions, ParsedTool } from './useSpringboardTools.types';
import { i18n } from '@/shared/i18n';

const parseAgent = (agent: AgentDto): ParsedTool => {
  const isUnconfigured = agent.configurationStatus?.configured === false;
  return {
    id: agent.id,
    title: agent.name || i18n.t('springboard:springboard.useSpringboardTools.untitledTool'),
    description: agent.description || '',
    disabled: isUnconfigured,
    disabledReason: isUnconfigured
      ? i18n.t('springboard:springboard.toolGrid.notConfigured')
      : undefined,
    isUnconfigured,
    ...(agent.visibility ? { visibility: agent.visibility } : {}),
  };
};

export const useSpringboardTools = ({ tabId, enabled = true }: UseSpringboardToolsOptions) => {
  const { canOpenTool } = usePermissions();

  const query = useQuery({
    queryKey: ['springboard-tools', tabId],
    queryFn: async () => {
      try {
        const response = await AgentsService.getAllAgents({
          query: { tag: tabId, includeNonVisibleAgents: false },
        });
        const agents = response.data ?? [];
        return agents.map(parseAgent);
      } catch (err: unknown) {
        logger.error('Failed to fetch agents for springboard:', err);
        return [] as ParsedTool[];
      }
    },
    enabled: enabled && !!tabId,
  });

  const tools = useMemo(() => {
    const agentTools = query.data ?? [];
    return agentTools.filter((tool) => canOpenTool(tool.id));
  }, [query.data, canOpenTool]);

  return {
    tools,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
  };
};
